<template>
    <div class="add-calendar-event">
        <header class="d-flex align-items-center font-size-lg px-3 py-2 bg-white add-calendar-event__header">
            <span class="fw-bold">{{ dialogTitle }}</span>
            <i class="ms-auto fas fa-times-circle text-danger cursor-pointer" @click="cancel()"></i>
        </header>
        <section
            v-if="mode === 'edit' && initialEventType === 'recurring'"
            class="d-flex align-items-center font-size-xs edit-info"
        >
            <i class="fas fa-info-circle text-secondary ms-2 me-2"></i>
            <div v-if="isEditSeries">You're editing this and all the following events in this series.</div>
            <template v-else>
                <div>You're editing this event in the series</div>
                <button type="button" class="edit-series-btn fw-semibold ms-auto bg-white" @click="isEditSeries = true">
                    Edit entire series
                </button>
            </template>
        </section>
        <section v-if="showConflictWarning" class="conflict-warning d-flex align-items-center font-size-xs">
            <i class="fas fa-info-circle ms-2 me-2"></i> Not everyone is available.
        </section>

        <section class="add-calendar-event__section">
            <label class="add-calendar-event__label">Title<span class="text-danger">*</span></label>
            <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v$.eventTitle.$error }"
                placeholder="Add Title"
                v-model="eventTitle"
                @blur="v$.eventTitle.$touch"
            />
            <span v-if="v$.eventTitle.$error" class="field-invalid">Title is required.</span>

            <label class="add-calendar-event__label mt-3">Participants<span class="text-danger">*</span></label>
            <TagsInput
                class="w-100"
                :class="{ 'is-invalid': v$.attendees.$error }"
                :getData="getUsersList"
                :tags="attendees"
                v-on:updateTags="updateTags"
                placeholder="Add Participants"
                :is-focus="true"
                :is-for-messaging="true"
                :show-user-conflict="mode === 'create' ? true : false"
                :show-pill="false"
                classes="w-100 form-group pt-1 mt-1"
                :focusOnInput="true"
                tabindex="1"
                :disabled="isEditingSingleOccurence"
            />
            <span v-if="isEditingSingleOccurence"
                >Participants can't be edited when updating a single occurrence. To modify participants, choose "Edit
                entire series"</span
            >
            <span v-if="v$.attendees.$error" class="field-invalid">At least one participant is required.</span>
        </section>

        <section class="add-calendar-event__section">
            <div class="d-flex justify-content-end align-items-center pb-2">
                <span class="me-2 fw-bold">All Day</span>
                <div class="form-check form-switch mb-0">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        :checked="isAllDayEvent"
                        @change="toggleAllDayEvent"
                    />
                </div>
            </div>

            <div class="row g-2 align-items-end">
                <div class="col-12 col-md-4">
                    <label class="add-calendar-event__label">Date<span class="text-danger">*</span></label>
                    <DatePicker
                        :getDate="changeDate"
                        :options="{
                            orientation: 'left bottom',
                            startDate: eventDate
                        }"
                    >
                        <div class="input-group date datepickerInput">
                            <input
                                type="text"
                                class="form-control"
                                :class="{ 'is-invalid': v$.eventDate.$error }"
                                name="eventDate"
                                placeholder="MM/DD/YYYY"
                                :value="formatDate(eventDate, 'MM/DD/YYYY')"
                                @blur="v$.eventDate.$touch"
                            />
                            <div class="input-group-addon input-group-append">
                                <div class="input-group-text">
                                    <i class="fa fa-calendar"></i>
                                </div>
                            </div>
                        </div>
                    </DatePicker>
                    <span v-if="v$.eventDate.$error" class="field-invalid">Date is required.</span>
                </div>

                <div class="col-12 col-md-4" v-if="!isAllDayEvent">
                    <label class="add-calendar-event__label">Start Time<span class="text-danger">*</span></label>

                    <MeetingTimePicker
                        v-model="startTime"
                        :minTime="startMinTime"
                        @update:model-value="checkUserConflicts(attendees)"
                    />
                </div>

                <div class="col-12 col-md-4" v-if="!isAllDayEvent">
                    <label class="add-calendar-event__label">End Time<span class="text-danger">*</span></label>

                    <MeetingTimePicker
                        v-model="endTime"
                        :minTime="endMinTime"
                        @update:model-value="checkUserConflicts(attendees)"
                    />
                </div>
            </div>

            <!-- Recurring events -->
            <div v-if="!isEditingSingleOccurence" class="d-flex align-items-end flex-wrap ps-1 mt-2 gap-2">
                <div class="w-40">
                    <label class="add-calendar-event__label">Repeat</label>
                    <select
                        class="form-select"
                        :value="recurrenceType"
                        @change="handleRecurrenceChange"
                        name="recurrence"
                    >
                        <option v-for="value of recurrenceTypeList" :key="value.value" :value="value.value">
                            {{ value.value === RecurrenceTypeEnum.None ? "Does not repeat" : value.name }}
                        </option>
                    </select>
                </div>
                <div v-if="showFrequency" class="d-flex align-items-center">
                    <span class="me-2">Every</span>
                    <select class="form-select" :value="frequency" @change="handleFrequencyChange" name="frequency">
                        <option v-for="i in frequencyCount" :key="i" :value="i">
                            {{ i }}
                        </option>
                    </select>
                    <span class="ms-2">{{ recurrenceType === RecurrenceTypeEnum.Weekly ? "weeks" : "months" }}</span>
                </div>

                <template v-if="isRecurringEvent || recurrenceType !== RecurrenceTypeEnum.None">
                    <template v-if="recurrenceType === RecurrenceTypeEnum.Weekly">
                        <div class="form-group w-100">
                            <label for="days" class="mb-2">Repeat On</label>
                            <DayPicker
                                :selected-days-string="daysOfWeek"
                                :initValue="initDayPickerValue"
                                :isForMeetingCalendar="true"
                                v-on:updateSelectedDays="updateDaysOfWeek"
                            >
                            </DayPicker>
                        </div>
                    </template>

                    <div v-if="recurrenceType === RecurrenceTypeEnum.Monthly" class="w-40">
                        <label for="on-date" class="add-calendar-event__label">On Date</label>
                        <DatePicker
                            :getDate="changeDate"
                            :options="{
                                orientation: 'left bottom',
                                startDate: eventDate
                            }"
                        >
                            <div class="input-group date datepickerInput">
                                <input
                                    type="text"
                                    class="form-control"
                                    name="onDate"
                                    placeholder="MM/DD/YYYY"
                                    :value="formatDate(eventDate, 'MM/DD/YYYY')"
                                    disabled
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                            </div>
                        </DatePicker>
                    </div>

                    <div class="w-40">
                        <label for="date" class="add-calendar-event__label">End Date</label>
                        <DatePicker
                            :getDate="changeDate"
                            :options="{
                                orientation: 'left top',
                                startDate: eventDate
                            }"
                        >
                            <div class="input-group date datepickerInput pe-2">
                                <input
                                    type="text"
                                    class="form-control"
                                    name="recurrenceEndDate"
                                    placeholder="MM/DD/YYYY"
                                    :value="formatDate(recurrenceEndDate, 'MM/DD/YYYY')"
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                            </div>
                        </DatePicker>
                    </div>

                    <div class="mt-2">
                        {{ occurenceText(recurrenceStartDate, recurrenceEndDate, recurrenceType, daysOfWeek) }}
                    </div>
                </template>
            </div>
        </section>

        <section class="add-calendar-event__section">
            <label class="add-calendar-event__label">Description</label>
            <textarea class="form-control" placeholder="Enter description" rows="4" v-model="description"></textarea>
        </section>

        <footer class="d-flex justify-content-center py-3">
            <button type="button" class="btn btn-light border border-secondary bg-white me-2 w-30" @click="cancel()">
                Cancel
            </button>
            <button
                type="button"
                class="btn btn-primary border border-primary w-30"
                :disabled="v$.$invalid || !eventTitle || !attendees.length || !eventDate"
                @click="save()"
            >
                {{ submitLabel }}
            </button>
        </footer>
    </div>
</template>

<script lang="ts" src="./AddCalendarEvent.ts"></script>

<style src="./AddCalendarEvent.scss"></style>
